interface SnakeProps {
  snake: { head: number; tail: number; label: string; description: string };
  onContinue: () => void;
}

interface LadderProps {
  ladder: { bottom: number; top: number; label: string; description: string };
  onContinue: () => void;
}

export function SnakeModal({ snake, onContinue }: SnakeProps) {
  const drop = snake.head - snake.tail;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/90 backdrop-blur-md" />
      <div
        className="relative z-10 w-full max-w-md rounded-xl overflow-hidden"
        style={{
          background: "#0d0d0d",
          border: "1px solid rgba(255,68,68,0.35)",
          boxShadow: "0 0 40px rgba(255,68,68,0.08)",
        }}
      >
        {/* Alert header */}
        <div
          className="flex items-center justify-between px-5 py-3"
          style={{ background: "rgba(255,68,68,0.07)", borderBottom: "1px solid rgba(255,68,68,0.15)" }}
        >
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-[#ff4444] animate-pulse"></span>
              <span className="w-2 h-2 rounded-full bg-[#ff4444]/40"></span>
              <span className="w-2 h-2 rounded-full bg-[#ff4444]/20"></span>
            </div>
            <div className="h-4 w-px bg-white/10"></div>
            <span className="text-[10px] text-[#ff4444]/70 font-mono uppercase tracking-widest font-bold">
              FAILURE_PATTERN · SEVERITY_HIGH
            </span>
          </div>
          <span
            className="text-[9px] font-bold font-mono px-2 py-0.5 rounded"
            style={{ background: "rgba(255,68,68,0.15)", color: "#ff4444", border: "1px solid rgba(255,68,68,0.3)" }}
          >
            P1
          </span>
        </div>

        {/* Content */}
        <div className="px-6 py-5">
          {/* Title */}
          <div className="flex items-start gap-3 mb-5">
            <div
              className="flex-shrink-0 w-10 h-10 flex items-center justify-center rounded-lg"
              style={{ background: "rgba(255,68,68,0.1)", border: "1px solid rgba(255,68,68,0.25)" }}
            >
              <i className="ri-error-warning-line text-[#ff4444] text-lg"></i>
            </div>
            <div>
              <div className="text-[10px] text-white/25 font-mono uppercase tracking-widest mb-1">Failure Pattern Triggered</div>
              <h3 className="text-lg font-black text-white leading-tight">{snake.label}</h3>
            </div>
          </div>

          {/* Description */}
          <p className="text-sm text-white/55 leading-relaxed font-mono mb-5">{snake.description}</p>

          {/* Impact block */}
          <div
            className="rounded-lg p-4 mb-5"
            style={{ background: "rgba(255,68,68,0.05)", border: "1px solid rgba(255,68,68,0.2)" }}
          >
            <div className="text-[10px] text-[#ff4444]/60 font-mono uppercase tracking-widest mb-3">Impact Assessment</div>
            <div className="grid grid-cols-3 gap-3">
              <div className="text-center">
                <div className="text-xl font-black font-mono text-[#ff4444]">{snake.head}</div>
                <div className="text-[10px] text-white/25 font-mono">from sq</div>
              </div>
              <div className="flex items-center justify-center">
                <div className="flex flex-col items-center gap-1">
                  <i className="ri-arrow-down-double-line text-[#ff4444] text-lg"></i>
                  <span className="text-[10px] text-[#ff4444]/60 font-mono">-{drop} sq</span>
                </div>
              </div>
              <div className="text-center">
                <div className="text-xl font-black font-mono text-white/60">{snake.tail}</div>
                <div className="text-[10px] text-white/25 font-mono">to sq</div>
              </div>
            </div>
          </div>

          <button
            onClick={onContinue}
            className="w-full flex items-center justify-center gap-2 py-3 rounded-lg font-bold text-sm transition-all whitespace-nowrap cursor-pointer"
            style={{ background: "rgba(255,68,68,0.15)", color: "#ff4444", border: "1px solid rgba(255,68,68,0.3)" }}
            onMouseEnter={(e) => {
              (e.currentTarget as HTMLElement).style.background = "rgba(255,68,68,0.25)";
            }}
            onMouseLeave={(e) => {
              (e.currentTarget as HTMLElement).style.background = "rgba(255,68,68,0.15)";
            }}
          >
            <i className="ri-arrow-down-line"></i>
            Acknowledge &amp; Slide Down
          </button>
        </div>
      </div>
    </div>
  );
}

export function LadderModal({ ladder, onContinue }: LadderProps) {
  const climb = ladder.top - ladder.bottom;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/90 backdrop-blur-md" />
      <div
        className="relative z-10 w-full max-w-md rounded-xl overflow-hidden"
        style={{
          background: "#0d0d0d",
          border: "1px solid rgba(0,255,136,0.35)",
          boxShadow: "0 0 40px rgba(0,255,136,0.08)",
        }}
      >
        {/* Header */}
        <div
          className="flex items-center justify-between px-5 py-3"
          style={{ background: "rgba(0,255,136,0.06)", borderBottom: "1px solid rgba(0,255,136,0.15)" }}
        >
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-[#00ff88] animate-pulse"></span>
              <span className="w-2 h-2 rounded-full bg-[#00ff88]/40"></span>
              <span className="w-2 h-2 rounded-full bg-[#00ff88]/20"></span>
            </div>
            <div className="h-4 w-px bg-white/10"></div>
            <span className="text-[10px] text-[#00ff88]/70 font-mono uppercase tracking-widest font-bold">
              RELIABILITY_WIN · BEST_PRACTICE
            </span>
          </div>
          <span
            className="text-[9px] font-bold font-mono px-2 py-0.5 rounded"
            style={{ background: "rgba(0,255,136,0.15)", color: "#00ff88", border: "1px solid rgba(0,255,136,0.3)" }}
          >
            RESOLVED
          </span>
        </div>

        {/* Content */}
        <div className="px-6 py-5">
          {/* Title */}
          <div className="flex items-start gap-3 mb-5">
            <div
              className="flex-shrink-0 w-10 h-10 flex items-center justify-center rounded-lg"
              style={{ background: "rgba(0,255,136,0.1)", border: "1px solid rgba(0,255,136,0.25)" }}
            >
              <i className="ri-shield-check-line text-[#00ff88] text-lg"></i>
            </div>
            <div>
              <div className="text-[10px] text-white/25 font-mono uppercase tracking-widest mb-1">Reliability Win Unlocked</div>
              <h3 className="text-lg font-black text-white leading-tight">{ladder.label}</h3>
            </div>
          </div>

          {/* Description */}
          <p className="text-sm text-white/55 leading-relaxed font-mono mb-5">{ladder.description}</p>

          {/* Gain block */}
          <div
            className="rounded-lg p-4 mb-5"
            style={{ background: "rgba(0,255,136,0.04)", border: "1px solid rgba(0,255,136,0.2)" }}
          >
            <div className="text-[10px] text-[#00ff88]/60 font-mono uppercase tracking-widest mb-3">Position Update</div>
            <div className="grid grid-cols-3 gap-3">
              <div className="text-center">
                <div className="text-xl font-black font-mono text-white/60">{ladder.bottom}</div>
                <div className="text-[10px] text-white/25 font-mono">from sq</div>
              </div>
              <div className="flex items-center justify-center">
                <div className="flex flex-col items-center gap-1">
                  <i className="ri-arrow-up-double-line text-[#00ff88] text-lg"></i>
                  <span className="text-[10px] text-[#00ff88]/60 font-mono">+{climb} sq</span>
                </div>
              </div>
              <div className="text-center">
                <div className="text-xl font-black font-mono text-[#00ff88]">{ladder.top}</div>
                <div className="text-[10px] text-white/25 font-mono">to sq</div>
              </div>
            </div>
          </div>

          <button
            onClick={onContinue}
            className="w-full flex items-center justify-center gap-2 py-3 rounded-lg font-bold text-sm transition-all whitespace-nowrap cursor-pointer"
            style={{ background: "#00ff88", color: "#0a0a0a" }}
            onMouseEnter={(e) => {
              (e.currentTarget as HTMLElement).style.background = "#00e07a";
            }}
            onMouseLeave={(e) => {
              (e.currentTarget as HTMLElement).style.background = "#00ff88";
            }}
          >
            <i className="ri-arrow-up-line"></i>
            Climb Up — Apply Best Practice
          </button>
        </div>
      </div>
    </div>
  );
}
